import { ReactNode } from 'react';
import classnames from 'classnames';

import type { Icon } from '@personly/libs-types';

import RecordColumn from '../record-column/index.js';
import RecordRow from '../record-row/index.js';

type Orientation = 'row' | 'column';

const displayHeader = ({
  orientation,
  legend,
}: {
  orientation: Orientation;
  legend: boolean;
}) =>
  (orientation === 'row' && legend) || (orientation === 'column' && !legend);

const displayValue = ({ legend }: { legend: boolean }) => !legend;

type Datum = {
  axis: string;
  width?: number;
  icon?: Icon;
  header?: ReactNode;
  value?: ReactNode;
};

interface Props {
  children: (
    displayHeader: boolean,
    displayValue: boolean,
    datum: Datum,
    legend: boolean
  ) => ReactNode;
  data: Array<Datum>;
  first?: boolean;
  last?: boolean;
  legend?: boolean;
  orientation: Orientation;
}

const Record = ({
  children,
  data,
  first = false,
  last = false,
  legend = false,
  orientation,
}: Props) => {
  const className = classnames({
    flex: true,
    'flex-1': true,
    'border-l': true,
    'border-l-record-border-color': true,
    'border-r': true,
    'border-r-record-border-color': true,
    'border-t-0': true,
    'border-b-0': true,
    'rounded-t-none': true,
    'rounded-bl-none': true,
    hidden: legend && orientation === 'column',
    'flex-col': orientation === 'column',
    'flex-row': orientation === 'row',
    '!border-t': orientation === 'row',
    '!border-t-record-border-color': orientation === 'row',
    '!border-b': (last && !legend) || orientation === 'column',
    '!border-b-record-border-color':
      (last && !legend) || orientation === 'column',
    '!rounded-tl': (first && legend) || orientation === 'column',
    '!rounded-tr': (first && legend) || orientation === 'column',
    '!rounded-bl': (last && !legend) || orientation === 'column',
    '!rounded-br': (last && !legend) || orientation === 'column',
    'first:!mt-0': orientation === 'column',
    'mt-2': orientation === 'column',
  });

  return (
    <div className={className}>
      <RecordRow orientation={orientation}>
        {data.map((datum) => (
          <RecordColumn
            first={first}
            last={last}
            legend={legend}
            orientation={orientation}
            key={datum.axis}
            width={datum.width || undefined}
          >
            {children(
              displayHeader({ orientation, legend }),
              displayValue({ legend }),
              datum,
              legend
            )}
          </RecordColumn>
        ))}
      </RecordRow>
    </div>
  );
};

export default Record;
